<template>
  <main class="mb-[88px] md:mb-[76px]">
    <Loader
      :loader="isFormLoading"
      :label="$t('labels.generic', { pt: 'Carregando...', en: 'Loading...' })"
      loaderType="progress"
    />
    <BreadcrumbForum v-if="isTeacherOrAdmin() && !isFormLoading" />
    <section
      class="max-w-[1113px] mx-auto mt-6 custom-container-forum"
      v-if="!isFormLoading"
    >
      <button
        @click="backToForumView"
        class="flex items-center h-8 mb-8 btn-simple"
      >
        <v-icon name="md-arrowback" width="24" height="24" />
        <span v-if="!$route.params.topicId">
          {{
            $t("labels.generic", {
              pt: "Voltar para Fórum",
              en: "Back to Forum",
            })
          }}
        </span>
        <span v-else>
          {{
            $t("labels.generic", {
              pt: "Voltar para tópico",
              en: "Back to Topic",
            })
          }}
        </span>
      </button>

      <form @submit.prevent="submitTopicForm">
        <header class="flex justify-between items-center mb-6">
          <h2 class="text-2xl font-medium leading-7">
            {{
              !$route.params.topicId
                ? $t("forum.topic.form.title")
                : $t("forum.topic.form.editTitle")
            }}
          </h2>

          <div class="flex justify-end items-center gap-3">
            <Button type="reset" @click="backToForumView" class="btn-outline">
              {{ $t("forum.topic.form.buttons.cancel") }}
            </Button>

            <Button :disabled="isFormLoading" type="submit" class="btn-primary">
              {{ $t("forum.topic.form.buttons.publish") }}
            </Button>
          </div>
        </header>
        <fieldset>
          <label for="title" class="block mb-3">
            {{ $t("forum.topic.form.labels.title") }}
          </label>

          <InputText
            type="text"
            id="title"
            v-model="formTopic.title"
            :class="[{ 'p-invalid': v$.formTopic.title.$error }]"
            class="!mb-3"
            :maxlength="150"
          />

          <div class="w-full">
            <Errors
              :errors="v$.formTopic.title.$errors"
              :actualLength="formTopic.title?.length"
              :maxLength="150"
              :minLength="6"
            />
          </div>
        </fieldset>
        <fieldset
          v-if="
            courseType !== 'INTERACTIVE' && selectChapterOptions.length !== 0
          "
          class="h-[4.5rem] mb-8"
        >
          <label for="chapter" class="block mb-3">
            {{ $t("forum.topic.form.labels.tag") }}
          </label>

          <Dropdown
            :options="selectChapterOptions"
            class="w-full"
            v-model="formTopic.tag"
            optionLabel="name"
            optionValue="name"
            placeholder="Selecione o capítulo de referência"
          />
        </fieldset>
        <fieldset
          class="flex flex-col flex-wrap items-center max-w-full p-0 mb-8"
        >
          <div class="sm:w-sm md:w-md lg:w-[1008px] xl:w-[1113px]">
            <label for="description" class="block mb-3">
              {{ $t("forum.topic.form.labels.description") }}
            </label>

            <Editor
              v-model="formTopic.description"
              @text-change="getDescriptionLength"
              class="mb-3 w-[93vw] sm:w-[624px] md:w-[752px] lg:w-[992px] xl:w-[1113px] custom-comment-input"
              :class="[
                {
                  'border !border-red-600 rounded-md':
                    v$.formTopic.description.$error,
                },
              ]"
              editorStyle="height: 160px; max-width: inherit;"
            >
              <template v-slot:toolbar>
                <span class="ql-formats">
                  <button v-tooltip.bottom="'Bold'" class="ql-bold"></button>
                  <button
                    v-tooltip.bottom="'Italic'"
                    class="ql-italic"
                  ></button>
                  <button
                    v-tooltip.bottom="'Underline'"
                    class="ql-underline"
                  ></button>
                </span>

                <span class="ql-formats">
                  <button
                    v-tooltip.bottom="'Ordered'"
                    class="ql-list"
                    value="ordered"
                  ></button>
                  <button
                    v-tooltip.bottom="'Bullet'"
                    class="ql-list"
                    value="bullet"
                  ></button>
                  <select class="ql-align" />
                </span>

                <span class="ql-formats">
                  <button
                    v-tooltip.bottom="'Link'"
                    class="ql-link"
                    value="link"
                  ></button>
                  <button
                    v-tooltip.bottom="'CodeBlock'"
                    class="ql-code-block"
                  ></button>
                </span>
              </template>
            </Editor>

            <div class="w-full custom-comment-input">
              <Errors
                :errors="v$.formTopic.description.$errors"
                :actualLength="formTopic.description?.length"
                :maxLength="700"
              />
            </div>
          </div>
        </fieldset>

        <AddFileInput
          @onSetImage="setTopicImage"
          @onResetImage="resetTopicImage"
          :imageSelected="image"
        />
      </form>
    </section>
  </main>
</template>

<script lang="ts">
import { maxLength, minLength, required } from "@/locale/locale.config";
import { Chapter } from "@/models/chapter.model";
import { CourseType } from "@/models/courses.model";
import { TopicForm } from "@/models/topic-form.model";
import { Roles } from "@/models/user.model";
import { GenericsUtils } from "@/utils/generics.util";
import { GoToRoute } from "@/utils/go-to.util";
import { Messages } from "@/utils/messages.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { getUserFromStorage } from "@/utils/user-store.util";
import useVuelidate from "@vuelidate/core";
import { EditorTextChangeEvent } from "primevue/editor";
import { take } from "rxjs";
import { defineAsyncComponent, defineComponent } from "vue";
import { ForumService } from "../forum.service";

const AddFileInput = defineAsyncComponent(
  () =>
    import(
      "@/views/CourseStudent/Forum/components/InputImageFile/input-image-file.vue"
    )
);

const BreadcrumbForum = defineAsyncComponent(
  () =>
    import(
      "@/views/CourseStudent/Forum/components/BreadcrumbForum/breadcrumb-forum.vue"
    )
);

export default defineComponent({
  name: "add-topic",
  components: {
    AddFileInput,
    BreadcrumbForum,
  },
  data() {
    return {
      formTopic: new TopicForm(),
      descriptionLength: 0,
      selectChapterOptions: [] as Array<Chapter>,
      image: undefined as any,
      isFormLoading: true,
      courseType: CourseType[
        JSON.parse(String(localStorage.getItem("course-type")))
      ] as CourseType | null,
    };
  },
  mounted() {
    this.getChapters();
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      formTopic: {
        title: { required, max: maxLength(150), min: minLength(6) },
        description: { required, max: maxLength(700) },
      },
    };
  },
  methods: {
    submitTopicForm(): void {
      this.v$.formTopic.$validate();
      if (!this.v$.formTopic.$invalid) {
        const data = new TopicForm();
        data.id = this.formTopic?.id;
        data.title = this.formTopic.title;
        data.description = this.formTopic.description;
        data.tag = this.formTopic.tag;
        data.courseId = this.$route.params.id as string;
        data.mediaId = this.formTopic.mediaId;

        const appendName: any = { file: "file", data: "topic" };
        let topicForm = new FormData();

        if (data.mediaId) {
          topicForm = this.generics.fileBuild(data, null, appendName);
        } else {
          topicForm = this.generics.fileBuild(data, this.image, appendName);
        }

        if (this.$route.params.topicId) {
          this.updateTopic(topicForm);
        } else {
          this.saveTopic(topicForm);
        }
      }
    },
    saveTopic(topicForm: FormData): void {
      this.isFormLoading = true;

      this.service.topics.pipe().subscribe({
        next: (response) => {
          if (response) {
            this.$toast.add(ToastFactory.success(Messages.FORUM_TOPIC_SAVED));
            this.redirectToTopicDetail(response.id);
          }
          this.isFormLoading = false;
        },
      });

      this.service.createTopic(topicForm);
    },
    updateTopic(topicForm: FormData): void {
      this.isFormLoading = true;

      this.service.topic.pipe().subscribe({
        next: (response) => {
          if (response) {
            this.$toast.add(ToastFactory.success(Messages.FORUM_TOPIC_UPDATED));
            this.redirectToTopicDetail(response.id);
          }
          this.isFormLoading = false;
        },
      });

      this.service.updateTopic(this.$route.params.topicId as string, topicForm);
    },
    redirectToTopicDetail(newTopicId: string): void {
      const topicDetailRouteParams = {
        ...this.$route.params,
        topicId: newTopicId,
      };
      this.go.redirectToPage("topicDetail", topicDetailRouteParams);
    },
    setTopicImage(image: any): void {
      this.image = image;
      this.formTopic.mediaId = undefined;
    },
    resetTopicImage(): void {
      this.image = undefined;
      this.formTopic.mediaId = undefined;
    },
    getChapters() {
      if (this.courseType === "INTERACTIVE") {
        this.isFormLoading = false;
        return;
      }

      this.service.chapters.subscribe({
        next: (response) => {
          if (this.$route.params.topicId) {
            this.getTopicById(this.$route.params.topicId as string);
          }
          this.selectChapterOptions = response ?? [];
          this.isFormLoading = false;
        },
        error: () => {
          this.isFormLoading = false;
          this.selectChapterOptions = [];
        },
      });

      this.service.getChaptersForum(this.$route.params.id as string, "/graphs");
    },
    getDescriptionLength(event: EditorTextChangeEvent): void {
      this.descriptionLength = event.textValue.length;
    },

    backToForumView(): void {
      this.$router.go(-1);
    },
    getTopicById(topicId: string) {
      this.service.topic.pipe(take(1)).subscribe({
        next: (response) => {
          if (response.content) {
            this.formTopic = response.content[0].topic;
            this.formTopic.mediaId = response.content[0].topic.media?.id;
            this.setTagChapter(response.content[0].topic.tag);
            this.image = response.content[0].topic.media;
          }
        },
        error: () => {
          this.go.redirectToPage("forum");
        },
      });

      this.service.getTopicById(topicId);
    },
    setTagChapter(tag: string): void {
      const tagIndex = this.selectChapterOptions.findIndex(
        (chapter) => chapter.name.toLowerCase() === tag.toLowerCase()
      );
      this.formTopic.tag = this.selectChapterOptions[tagIndex].name;
    },
    isTeacherOrAdmin(): boolean {
      const userRole = getUserFromStorage()?.role;
      return userRole === Roles.ADMINISTRATOR || userRole === Roles.TEACHER;
    },
  },
  computed: {
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
    service(): ForumService {
      return new ForumService();
    },
    isChapterOptionsPresent(): boolean {
      return this.selectChapterOptions.length > 0;
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
  },
});
</script>
